import React, { FC, useMemo } from 'react';
import { Form, Radio, Select, Switch, TreeSelect } from 'antd';
import ProblemTypeTreeSelect from '../problem_type_tree_select';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesRestCrudThunksForBuyer,
} from '../../thunks/problem_types_thunks';
import {
	fetchSupplierFacilitiesInPrivateNetworkForBuyer,
	supplierFacilitiesRestCrudThunksForBuyer,
} from '../../thunks/supplier_facilities_thunks';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { getApprovalTypesTracketOptions } from '../../utils/BudgetUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';

const WORK_ORDER_FORM_PROBLEM_TYPES_AUTOCOMPLETE = 'WORK_ORDER_FORM_PROBLEM_TYPES_AUTOCOMPLETE';
const BUDGETS_FORM_SUPPLIERS_AUTOCOMPLETE = 'BUDGETS_FORM_SUPPLIERS_AUTOCOMPLETE';

const BudgetSecondSteps: FC<any> = ({
	step,
	form,
	formData,

	problemTypes,
	fetchProblemTypes,
	fetchMultipleProblemTypes,

	supplierFacilities,
	fetchSupplierFacilitiesInPrivateNetwork,
	fetchMultipleSupplierFacilitiesInPrivateNetwork,
}): React.ReactElement => {
	const { setFieldsValue } = form;

	const values = Form.useWatch([], form);

	const hidden = useMemo(() => step !== 1, [step]);

	const onlyCompletedWorkOrders = nullSafeGet('trackOnlyCompletedWorkOrders', values);
	const removePendingWorkOrders = (value) => {
		if (value) {
			let array = (values as any).accrualTypesTracked;
			if (array.indexOf('pending_workorders') >= 0) {
				array.splice(array.indexOf('pending_workorders'), 1);
				setFieldsValue({ accrualTypesTracked: array });
			}
		}
	};

	return (
		<>
			<Form.Item
				hidden={hidden}
				name="trackOnlyCompletedWorkOrders"
				label="Only Accrue Completed Work Orders"
				initialValue={formData.trackOnlyCompletedWorkOrders || true}
				valuePropName={'checked'}
			>
				<Switch onChange={removePendingWorkOrders} />
			</Form.Item>
			<Form.Item
				hidden={hidden}
				name="accrualTypesTracked"
				label="Accrued Types Tracked"
				initialValue={[]}
				rules={[{ required: false, message: 'Please select a record type to track.' }]}
			>
				<TreeSelect
					mode={'multiple'}
					treeCheckable={'multiple'}
					treeData={getApprovalTypesTracketOptions(onlyCompletedWorkOrders)}
					treeNodeLabelProp="label"
				/>
			</Form.Item>
			<Form.Item hidden={hidden} name={'isPM'} label={'Work Order Type'} initialValue={undefined}>
				<Radio.Group>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={undefined}>
						Any
					</Radio.Button>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={true}>
						Planned
					</Radio.Button>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={false}>
						Reactive
					</Radio.Button>
				</Radio.Group>
			</Form.Item>
			<Form.Item
				hidden={hidden}
				label={'Expenditure Type'}
				name={'isCapex'}
				initialValue={undefined}
			>
				<Radio.Group>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={undefined}>
						Any
					</Radio.Button>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={false}>
						R&M
					</Radio.Button>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={true}>
						CapEx
					</Radio.Button>
				</Radio.Group>
			</Form.Item>
			<Form.Item
				hidden={hidden}
				name="problemTypeIds"
				label="Problem Type"
				initialValue={undefined}
			>
				<ProblemTypeTreeSelect
					mode="multiple"
					stateSlice={problemTypes}
					targetCollectionName={WORK_ORDER_FORM_PROBLEM_TYPES_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleProblemTypes(ids, targetCollectionName);
					}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) =>
						fetchProblemTypes(
							{ name: searchText },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						)
					}
					additionalFilters={{}}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item
				hidden={hidden}
				name="supplierFacilityIds"
				label="Supplier Facilities"
				initialValue={undefined}
			>
				<OWAsyncSelect
					mode="multiple"
					allowClear={true}
					stateSlice={supplierFacilities}
					targetCollectionName={BUDGETS_FORM_SUPPLIERS_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleSupplierFacilitiesInPrivateNetwork(ids, targetCollectionName);
					}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchSupplierFacilitiesInPrivateNetwork(
							{ name: searchText },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					renderRecord={(supplierFacility) => (
						<Select.Option key={supplierFacility.id} value={supplierFacility.id}>
							{supplierFacility.name}
						</Select.Option>
					)}
					additionalFilters={{}}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
		</>
	);
};

const mapStateToProps = (state) => ({
	supplierFacilities: state.supplier_facilities,
	problemTypes: state.problem_types,
});

const mapDispatchToProps = (dispatch) => ({
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			problemTypesHeirarchicalForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchSupplierFacilitiesInPrivateNetwork: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters
	) =>
		dispatch(
			fetchSupplierFacilitiesInPrivateNetworkForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters
			)
		),
	fetchMultipleSupplierFacilitiesInPrivateNetwork: (ids, targetCollectionName) =>
		dispatch(supplierFacilitiesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(BudgetSecondSteps as any)
);
